import { useEffect, useRef, useState } from 'react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'

type DebounceInputProps = {
    value: string | undefined
    debounceTimeoutLength?: number
    placeholder?: string
    callbackFn: (value: string) => void
} & Omit<
    React.ComponentPropsWithoutRef<'input'>,
    'value' | 'onChange' | 'placeholder'
>

const useDebouncedInput = <E extends HTMLInputElement | HTMLTextAreaElement>(
    value: string | undefined,
    callbackFn: (value: string) => void,
    debounceTimeoutLength: number,
) => {
    const normalizedValue = value || ''
    const [inputValue, setInputValue] = useState<string>(normalizedValue)
    const lastValueRef = useRef(inputValue)

    // Resync when `value` changes for a reason other than this hook's own callbackFn
    // (e.g. undo/redo, or a future chat-bot edit, overwriting the section externally
    // while this input stays mounted). Typing itself never triggers this: `value` only
    // changes once callbackFn has already been called with the same text. Adjusted
    // during render (not an effect) per React's guidance for resetting state from props.
    const [prevValue, setPrevValue] = useState(normalizedValue)
    if (normalizedValue !== prevValue) {
        setPrevValue(normalizedValue)
        lastValueRef.current = normalizedValue
        setInputValue(normalizedValue)
    }

    useEffect(() => {
        // Only trigger callback if the value actually changed
        if (lastValueRef.current === inputValue) {
            return
        }

        const debounceTimeout = setTimeout(() => {
            // Re-check: a blur may have already flushed this value
            if (lastValueRef.current === inputValue) return
            lastValueRef.current = inputValue
            callbackFn(inputValue)
        }, debounceTimeoutLength)

        return () => clearTimeout(debounceTimeout)
    }, [inputValue, debounceTimeoutLength, callbackFn])

    const onChange = (e: React.ChangeEvent<E>) => {
        setInputValue(e.target.value)
    }

    const onBlur = (e: React.FocusEvent<E>) => {
        if (e.target.value === lastValueRef.current) return
        lastValueRef.current = e.target.value
        callbackFn(e.target.value)
    }

    return { inputValue, onChange, onBlur }
}

export const DebounceInput: React.FC<DebounceInputProps> = ({
    value,
    debounceTimeoutLength = 1000,
    placeholder = 'Enter text...',
    callbackFn,
    className,
    ...props
}) => {
    const { inputValue, onChange, onBlur } =
        useDebouncedInput<HTMLInputElement>(
            value,
            callbackFn,
            debounceTimeoutLength,
        )

    return (
        <Input
            onChange={onChange}
            onBlur={onBlur}
            value={inputValue}
            placeholder={placeholder}
            className={cn('bg-background', className)}
            {...props}
        />
    )
}

type DebounceTextAreaProps = {
    value: string | undefined
    debounceTimeoutLength?: number
    placeholder?: string
    callbackFn: (value: string) => void
} & Omit<
    React.ComponentPropsWithoutRef<'textarea'>,
    'value' | 'onChange' | 'placeholder'
>

export const DebounceTextArea: React.FC<DebounceTextAreaProps> = ({
    value,
    debounceTimeoutLength = 1000,
    placeholder = 'Enter text...',
    callbackFn,
    style = {},
    className,
    ...props
}) => {
    const { inputValue, onChange, onBlur } =
        useDebouncedInput<HTMLTextAreaElement>(
            value,
            callbackFn,
            debounceTimeoutLength,
        )
    const textAreaRef = useRef<HTMLTextAreaElement | null>(null)

    useEffect(() => {
        if (textAreaRef.current) {
            textAreaRef.current.style.height = 'auto'
            textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`
        }
    }, [inputValue])

    return (
        <Textarea
            ref={textAreaRef}
            onChange={onChange}
            onBlur={onBlur}
            value={inputValue}
            placeholder={placeholder}
            className={cn('resize-none bg-background', className)}
            style={{ ...style, overflow: 'hidden' }}
            {...props}
        />
    )
}
